import React, {useState} from 'react';
import {StorageObject} from '../../../models/StorageObject';
import {BreadcrumbsDTO} from '../../../models/dto/BreadcrumbsDTO';
import {TabButton, TabNavigation, TabNavigationWrapper} from './FolderMetaDataTabView.style';
import {getTab} from '../../../utils/TabUtils';
import {FolderMetaDataTabMain} from './FolderMetaDataTabMain';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {MetaDataTab} from '../MetaDataTab';
import {FolderMetaDataTabAdditional} from './FolderMetaDataTabAdditional';
import {FolderMetaDataTabPermissions} from './FolderMetaDataTabPermissions';
import {FolderMetaDataTabLocales} from './FolderMetaDataTabLocales';
import EventEmitter from 'events';
import {Label} from '../../../models/Label';
import {t} from 'i18next';

export interface FolderMetaDataTabViewProps {
    readonly item: StorageObject;
    readonly breadcrumbs: BreadcrumbsDTO[];
    readonly currentPath: string;
    readonly rolesDataSource: string[];
    readonly parentOwnerRoles: string[];
    readonly parentRoles: string[];
    readonly parentLocales: string[];
    readonly language: string;
    readonly defaultLocale: string;
    readonly labelsInitialState: Map<string, Label>;
    readonly versionInitialState: string;
    readonly eventEmitter: EventEmitter;
}

const getTabs = (): string[] => {
    let tabs: string[] = [MetaDataTab.MAIN, MetaDataTab.ADDITIONAL, MetaDataTab.PERMISSIONS, MetaDataTab.LOCALES];

    return tabs;
}

/**
 * @name FolderMetaDataTabView
 * @description
 * The dialog to add the metadata of a newly created folder or edited folder
 */
export const FolderMetaDataTabView: React.VFC<FolderMetaDataTabViewProps> = (props) => {
    let tabs: string[] = getTabs();
    const [activeTab, setActiveTab] = useState(getTab(MetaDataTab.MAIN, tabs));

    const handleTabClick = (tab: React.SetStateAction<string>) => {
        setActiveTab(tab);
    };

    return (
        <TabNavigationWrapper id={generateIdentifier()} className='a-tab-navigation__wrapper'>
            <div id={generateIdentifier()} className='a-tab-navigation__gradients'>
                <TabNavigation id={generateIdentifier()} className='a-tab-navigation' role='tablist'>
                    {tabs.map((tab, index) => (
                        <li id={generateIdentifier(`'data-tab-item-'${index}`, '-')}
                            key={`'data-tab-item-'${index}`}
                            className='a-tab-navigation__item'
                            role='none'>
                            <TabButton id={generateIdentifier(`'data-tab-'${tab}`, '-')}
                                       type='button'
                                       className={`a-tab-navigation__tab ${activeTab === tab ? '-selected' : ''}`}
                                       data-frok-tab-identifier={`${index + 1}`}
                                       tabIndex={index}
                                       role='tab'
                                       aria-controls={`'data-tab-'${tab}`}
                                       onClick={() => handleTabClick(tab)}>
                                    <span id={generateIdentifier()} className='a-tab-navigation__tab-content'>
                                        <span id={generateIdentifier()}
                                              className='a-tab-navigation__label'>{t(tab)}</span>
                                    </span>
                            </TabButton>
                        </li>
                    ))}
                </TabNavigation>
                {(MetaDataTab.MAIN === activeTab) &&
                <FolderMetaDataTabMain item={props.item}
                                       parentLocales={props.parentLocales}
                                       language={props.language}
                                       defaultLocale={props.defaultLocale}
                                       labelsInitialState={props.labelsInitialState}
                                       versionInitialState={props.versionInitialState}
                                       eventEmitter={props.eventEmitter}/>
                }
                {(MetaDataTab.ADDITIONAL === activeTab) &&
                <FolderMetaDataTabAdditional dateValidFrom={props.item.metadata.dateValidFrom}
                                             dateValidTo={props.item.metadata.dateValidTo}
                                             tags={props.item.metadata.tags}
                                             eventEmitter={props.eventEmitter}/>
                }
                {(MetaDataTab.PERMISSIONS === activeTab) &&
                <FolderMetaDataTabPermissions isPublic={props.item.isPublic}
                                              ownerRoles={props.item.metadata.ownerRoles}
                                              roles={props.item.metadata.roles}
                                              parentOwnerRoles={props.parentOwnerRoles}
                                              parentRoles={props.parentRoles}
                                              rolesDataSource={props.rolesDataSource}
                                              eventEmitter={props.eventEmitter}/>
                }
                {(MetaDataTab.LOCALES === activeTab) &&
                <FolderMetaDataTabLocales labels={props.item.labels}
                                          parentLocales={props.parentLocales}
                                          defaultLocale={props.defaultLocale}
                                          labelsInitialState={props.labelsInitialState}
                                          eventEmitter={props.eventEmitter}/>
                }
            </div>
        </TabNavigationWrapper>
    );
}